import React, { useState } from 'react';
import { X, Star } from 'lucide-react';
import { api } from '../lib/axios';
import { toast } from 'react-hot-toast';
import { clsx } from 'clsx';
import { Button } from './Button';
import { useTranslation } from "react-i18next";

interface ReviewModalProps {
    appointment: any;
    isOpen: boolean;
    onClose: (completed?: boolean) => void;
}

export const ReviewModal: React.FC<ReviewModalProps> = ({ appointment, isOpen, onClose }) => {
    const { t } = useTranslation();
    const [rating, setRating] = useState<number>(0);
    const [hover, setHover] = useState<number>(0);
    const [comment, setComment] = useState('');
    const [submitting, setSubmitting] = useState(false);

    if (!isOpen || !appointment) return null;

    const handleSubmit = async (e?: React.FormEvent) => {
        if (e) e.preventDefault();
        if (rating === 0) {
            toast.error('Please select a rating');
            return;
        }

        setSubmitting(true);
        try {
            await api.post(`/appointments/${appointment.id}/review`, {
                rating,
                comment
            });
            toast.success('Thank you for your feedback!');
            onClose(true);
        } catch (error) {
            console.error('Failed to submit review', error);
            toast.error('Failed to submit review');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-slate-950/40 backdrop-blur-xl z-[200] flex items-center justify-center p-4 animate-in fade-in duration-300">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200">
                <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-slate-50">
                    <h2 className="font-bold text-slate-800 tracking-tight">{t('ui_rate_your_visit_626cef', `Rate Your Visit`)}</h2>
                    <button onClick={() => onClose(false)} className="p-1 hover:bg-slate-200 rounded-full transition-colors">
                        <X size={18} className="text-slate-500" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-6">
                    <div className="text-center">
                        <p className="text-sm text-slate-500 mb-4 uppercase tracking-widest font-black text-[10px]">
                            {t('ui_how_was_your_pe_f9ee3c', `How was your pet's stay with`)}{appointment.doctor?.clinicName || 'the Clinic'}?
                        </p>
                        
                        <div className="flex justify-center gap-2">
                            {[1, 2, 3, 4, 5].map((star) => (
                                <div
                                    key={star}
                                    className="relative cursor-pointer transition-transform hover:scale-110 active:scale-95"
                                    onMouseMove={(e) => {
                                        const rect = e.currentTarget.getBoundingClientRect();
                                        const x = e.clientX - rect.left;
                                        const val = x < rect.width / 2 ? star - 0.5 : star;
                                        setHover(val);
                                    }}
                                    onMouseLeave={() => setHover(0)}
                                    onClick={() => setRating(hover)}
                                >
                                    <Star
                                        size={36}
                                        className={clsx(
                                            "transition-colors duration-200",
                                            (hover || rating) >= star 
                                                ? "text-yellow-400 fill-yellow-400" 
                                                : (hover || rating) >= star - 0.5
                                                    ? "text-yellow-400"
                                                    : "text-slate-200"
                                        )}
                                    />
                                    {((hover || rating) >= star - 0.5 && (hover || rating) < star) && (
                                        <div className="absolute inset-0 overflow-hidden w-[50%] pointer-events-none">
                                            <Star size={36} className="text-yellow-400 fill-yellow-400" />
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                        
                        <div className="mt-2 h-4">
                            {rating > 0 && (
                                <span className="text-xs font-bold text-yellow-600 uppercase tracking-widest">
                                    {rating} {t('ui_star_rating_3d9d62', `Star Rating`)}</span>
                            )}
                        </div>
                    </div>

                    <div>
                        <label className="block text-[10px] font-black text-slate-400 mb-2 uppercase tracking-widest ml-1">
                            {t('ui_feedback_commen_eb5bbd', `Feedback Comment (Optional)`)}</label>
                        <textarea
                            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500 transition-all text-sm text-slate-700 min-h-[100px] resize-none"
                            placeholder={t('ui_share_your_expe_932ad4', `Share your experience...`)}
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                        />
                    </div>

                    <Button
                        onClick={handleSubmit}
                        fullWidth
                        disabled={submitting || rating === 0}
                        className="bg-indigo-600 text-white font-bold rounded-2xl shadow-xl hover:bg-indigo-700 transition-all h-12 border-transparent"
                    >
                        {submitting ? 'Submitting...' : `Submit ${rating || ''} Star Review`}
                    </Button>
                </form>
            </div>
        </div>
    );
};



